import { useTranslation } from 'react-i18next';
import {
    HStack,
    VStack,
    Text,
    Button,
    Checkbox,
    Box,
    useToast,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { RefreshCw, Download } from 'lucide-react';
import { useAppStore } from '../../state/store';
import { useBIMStore } from '../../state/useBIMStore';
import { ManageDialogFrame } from '../manage/ManageDialogFrame';
import { usePrecastConfigStore } from '../../state/usePrecastConfigStore';
import { downloadTextFile } from '../../services/fileSystem/fileAccess';
import { BIMElement, PrecastFloorElement, PrecastMountingPartElement, PrecastReinforcementElement, PrecastWallElement } from '../../core/types';
import { buildShopDrawingSvg, panelSizeMm } from '../../utils/precastFabrication';
import { wallPanelWeightMeters, floorPanelWeightMeters } from '../../utils/precastGeometry';

/** All precast panels present in the open model (wall / floor members). */
export function collectPrecastPanels(elements: Map<string, BIMElement>): Array<PrecastWallElement | PrecastFloorElement> {
    const out: Array<PrecastWallElement | PrecastFloorElement> = [];
    elements.forEach((el) => {
        if (el.category === 'Precast Walls') out.push(el as PrecastWallElement);
        else if (el.category === 'Precast Floors') out.push(el as PrecastFloorElement);
    });
    return out;
}

export function collectPrecastAttachments(elements: Map<string, BIMElement>) {
    const { t } = useTranslation();
    const mounting: PrecastMountingPartElement[] = [];
    const reinf: PrecastReinforcementElement[] = [];
    elements.forEach((el) => {
        if (el.category === 'Precast Mounting Parts') mounting.push(el as PrecastMountingPartElement);
        else if (el.category === 'Precast Reinforcement') reinf.push(el as PrecastReinforcementElement);
    });
    return { mounting, reinf };
}

/**
 * Precast Shop Drawings dialog (BIM: Precast > Fabrication > Shop Drawings).
 * Derives one fabrication sheet (SVG) per selected panel with outline, overall
 * dimensions, joint gaps, embedded parts, reinforcement and the concrete/steel
 * takeoff — then downloads them (single file or one sheet per panel).
 */
export function PrecastShopDrawingsDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.manageDialog === 'precastShopDrawings');
    const onClose = useAppStore((state) => state.closeManageDialog);
    const project = useBIMStore((state) => state.project);
    const config = usePrecastConfigStore((state) => state.config);
    const toast = useToast();

    const panels = useMemo(() => collectPrecastPanels(project.elements), [project]);
    const attachments = useMemo(() =>collectPrecastAttachments(project.elements), [project]); const [selected, setSelected] = useState<Set<string>>(new Set());
    const isSelected = (id: string) => selected.size === 0 || selected.has(id);
    const toggle = (id: string) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const activePanels = useMemo(
        () => panels.filter(p => isSelected(p.id)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [panels, selected],
    );

    const sheets = useMemo(() => {
        if (!isOpen) return [];
        return activePanels.map((panel, i) => ({
            panel,
            sheetNumber: `PD-${String(i + 1).padStart(2, '0')}`,
            svg: buildShopDrawingSvg(
                panel,
                attachments.mounting,
                attachments.reinf,
                { config, projectName: project.name || 'Project', sheetNumber: `PD-${String(i + 1).padStart(2, '0')}` },
            ),
        }));
    }, [isOpen, activePanels, attachments, config, project.name]);

    const downloadAll = () => {
        if (sheets.length === 0) {
            toast({ title: 'No precast panels to draw', status: 'warning', duration: 2500 });
            return;
        }
        sheets.forEach(s => downloadTextFile(`shop-drawing-${s.panel.geometryData.mark}.svg`, s.svg, 'image/svg+xml'));
        toast({ title: `${sheets.length} shop drawing sheet(s) exported`, status: 'success', duration: 3000 });
    };

    if (isOpen && panels.length === 0) {
        return (
            <ManageDialogFrame title={t('misc.precastShopDrawings')} isOpen={isOpen} onClose={onClose} size="md">
                <VStack align="stretch" spacing={3} py={4}>
                    <Text fontSize="sm">{t('misc.noPrecastPanelsFoundInTheModel')}</Text>
                    <Text fontSize="xs" color="text.secondary">{t('misc.usePrecastSplitOnAWallOrFloorSlabFirstTheGeneratedPanelsAreLis')}</Text>
                </VStack>
            </ManageDialogFrame>
        );
    }

    return (
        <ManageDialogFrame
            title={t('misc.precastShopDrawings')}
            isOpen={isOpen}
            onClose={onClose}
            size="3xl"
            footer={
                <HStack justify="space-between" w="full">
                    <Text fontSize="xs" color="text.secondary">
                        {activePanels.length} of {panels.length} panels · {config.shopSheetSizeId} · 1:{config.shopDrawingScale}
                    </Text>
                    <HStack>
                        <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} color="var(--color-text-secondary)">{t('misc.selectAll')}</Button>
                        <Button size="sm" colorScheme="blue" leftIcon={<Download size={14} />} onClick={downloadAll} isDisabled={sheets.length === 0}>{t('misc.exportSheetsSVG')}</Button>
                    </HStack>
                </HStack>
            }
        >
            <HStack align="start" spacing={4} h="100%">
                {/* Panel list */}
                <VStack align="stretch" spacing={1} w="280px" flexShrink={0} maxH="420px" overflowY="auto" pr={1}>
                    {panels.map(p => {
                        const size = panelSizeMm(p);
                        const weight = p.category === 'Precast Walls'
                            ? wallPanelWeightMeters(p as PrecastWallElement, config.concreteDensityKgM3)
                            : floorPanelWeightMeters(p as PrecastFloorElement, config.concreteDensityKgM3);
                        return (
                            <Checkbox key={p.id} size="sm" isChecked={isSelected(p.id)} onChange={() => toggle(p.id)}>
                                <Box>
                                    <Text fontSize="xs" fontWeight="bold">{p.geometryData.mark}</Text>
                                    <Text fontSize="10px" color="text.secondary">
                                        {p.type} · {Math.round(size.width)}×{Math.round(size.height)}×{Math.round(size.thickness)} mm · {(weight / 1000).toFixed(2)} t
                                    </Text>
                                </Box>
                            </Checkbox>
                        );
                    })}
                </VStack>
                {/* Sheet preview stack */}
                <VStack flex={1} align="center" spacing={3} maxH="420px" overflowY="auto">
                    {sheets.map(s => (
                        <Box key={s.panel.id} w="full" maxW="720px" borderWidth="1px" borderColor="border.base" borderRadius="sm" p={1} bg="white">
                            <Box
                                as="img"
                                src={`data:image/svg+xml;utf8,${encodeURIComponent(s.svg)}`}
                                alt={`Shop drawing ${s.panel.geometryData.mark}`}
                                w="full"
                                h="auto"
                            />
                        </Box>
                    ))}
                    {sheets.length === 0 && (
                        <VStack pt={8} spacing={2}>
                            <RefreshCw size={22} />
                            <Text fontSize="xs" color="text.secondary">{t('misc.selectPanelsToDeriveTheirShopSheets')}</Text>
                        </VStack>
                    )}
                </VStack>
            </HStack>
        </ManageDialogFrame>
    );
}
